/**
 * By Lucia PDP — interactions
 *  - Gallery thumbnail swap (image / video)
 *  - Quantity controls
 *  - Variant swatch switching (resolves variant via /products/{handle}.js)
 *  - FBT bundle add-to-cart (single /cart/add.js POST)
 */
(function () {
  'use strict';

  var moneyFormat = (window.Shopify && window.Shopify.money_format) || '${{amount}}';

  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10);
    if (isNaN(cents)) return '';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyFormat
      .replace('{{amount}}', whole + '.' + parts[1])
      .replace('{{amount_no_decimals}}', whole)
      .replace('{{amount_with_comma_separator}}', whole + ',' + parts[1])
      .replace('{{amount_no_decimals_with_comma_separator}}', whole);
  }

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ─── Cart: AJAX add + open the native drawer ──────────────────
   * Replaces the form's native POST-to-/cart redirect. We add via
   * /cart/add.js (which Klaviyo's onsite tracking hooks for the
   * "Added to Cart" / active-on-site event), fire Horizon's own
   * `cart:update` event so the cart items + icon re-render, then call
   * the cart drawer's public open() method directly. The drawer also
   * carries the `auto-open` attribute now, but we call open() too so
   * the behaviour never depends on that attribute alone.
   */
  function cartSectionIds() {
    return $$('cart-items-component')
      .map(function (el) { return el.getAttribute('data-section-id'); })
      .filter(Boolean);
  }

  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    return false;
  }

  function dispatchCartUpdate(cart, sections, sourceId) {
    // Mirrors Horizon's CartAddEvent: type 'cart:update', detail shape
    // { resource, sourceId, data: { source, sections } }.
    var detail = {
      resource: cart,
      sourceId: sourceId,
      data: { source: 'product-form-component', sections: sections },
    };
    var evt;
    try {
      evt = new CustomEvent('cart:update', { bubbles: true, detail: detail });
    } catch (e) {
      evt = document.createEvent('CustomEvent');
      evt.initCustomEvent('cart:update', true, false, detail);
    }
    document.dispatchEvent(evt);
  }

  // body: { id, quantity } or { items: [...] }. onError(payload) optional.
  function addToCart(body, sourceId, onError) {
    var ids = cartSectionIds();
    if (ids.length) {
      body.sections = ids.join(',');
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (r) {
        return r.json().then(function (data) {
          return { ok: r.ok, data: data };
        });
      })
      .then(function (res) {
        if (!res.ok || res.data.status) {
          if (onError) onError(res.data);
          return null;
        }
        // Refresh drawer line items + cart icon from the add response's
        // rendered sections, then open the drawer. No second /cart.js
        // round-trip — cart-items re-renders itself if sections is absent.
        dispatchCartUpdate(res.data, res.data.sections, sourceId);
        if (!openCartDrawer()) {
          window.location.href = '/cart';
        }
        return res.data;
      });
  }

  /* ─── Gallery: scroll-snap swipe + thumb sync + tap-to-zoom ──── */
  var zoomDialog = null;

  function openZoom(src, alt) {
    if (!zoomDialog) {
      zoomDialog = document.createElement('dialog');
      zoomDialog.className = 'gallery-zoom';
      zoomDialog.innerHTML =
        '<button type="button" class="gallery-zoom__close" aria-label="Close">×</button>' +
        '<div class="gallery-zoom__scroller"><img class="gallery-zoom__img" alt=""></div>';
      document.body.appendChild(zoomDialog);
      zoomDialog.querySelector('.gallery-zoom__close')
        .addEventListener('click', function () { closeZoom(); });
      // Tap the backdrop / margins (not the image) to dismiss.
      zoomDialog.addEventListener('click', function (e) {
        if (e.target === zoomDialog || e.target.classList.contains('gallery-zoom__scroller')) closeZoom();
      });
      zoomDialog.addEventListener('close', function () {
        document.documentElement.classList.remove('pdp-zoom-open');
      });
    }
    var zimg = zoomDialog.querySelector('.gallery-zoom__img');
    zimg.src = src;
    zimg.alt = alt || '';
    if (typeof zoomDialog.showModal === 'function' && !zoomDialog.open) {
      zoomDialog.showModal();
    } else {
      zoomDialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('pdp-zoom-open');
  }

  function closeZoom() {
    if (!zoomDialog) return;
    if (typeof zoomDialog.close === 'function' && zoomDialog.open) zoomDialog.close();
    else zoomDialog.removeAttribute('open');
    document.documentElement.classList.remove('pdp-zoom-open');
  }

  function initGallery(root) {
    var track = $('[data-gallery-main]', root);
    if (!track) return;
    var slides = $$('[data-gallery-slide]', track);
    var thumbs = $$('[data-gallery-thumb]', root);
    if (!slides.length) return;

    function slideByIndex(index) {
      for (var i = 0; i < slides.length; i++) {
        if (parseInt(slides[i].getAttribute('data-index'), 10) === index) return slides[i];
      }
      return null;
    }

    function setActiveThumb(index) {
      thumbs.forEach(function (t) {
        t.classList.toggle('active', parseInt(t.getAttribute('data-index'), 10) === index);
      });
    }

    // Thumbnail click → scroll the track to that slide (desktop behaviour).
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var index = parseInt(thumb.getAttribute('data-index'), 10);
        var slide = slideByIndex(index);
        if (!slide) return;
        track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        setActiveThumb(index);
      });
    });

    // Swiping/scrolling the track → keep the active thumbnail in sync.
    var raf = null;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = window.requestAnimationFrame(function () {
        raf = null;
        var center = track.scrollLeft + track.clientWidth / 2;
        var best = null, bestDist = Infinity;
        slides.forEach(function (s) {
          var d = Math.abs((s.offsetLeft + s.offsetWidth / 2) - center);
          if (d < bestDist) { bestDist = d; best = s; }
        });
        if (best) setActiveThumb(parseInt(best.getAttribute('data-index'), 10));
      });
    }, { passive: true });

    // Tap an image slide → open the zoom lightbox (video slides excluded).
    slides.forEach(function (slide) {
      if (slide.classList.contains('gallery__slide--video')) return;
      slide.addEventListener('click', function () {
        var src = slide.getAttribute('data-zoom-src');
        if (!src) return;
        var img = slide.querySelector('img');
        openZoom(src, img ? img.alt : '');
      });
    });
  }

  /* ─── Quantity ─────────────────────────────────────────────── */
  function initQty(root) {
    var input = $('[data-qty-input]', root);
    if (!input) return;
    var dec = $('[data-qty-decrement]', root);
    var inc = $('[data-qty-increment]', root);
    if (dec) dec.addEventListener('click', function () {
      var v = parseInt(input.value, 10) || 1;
      if (v > 1) input.value = v - 1;
    });
    if (inc) inc.addEventListener('click', function () {
      var v = parseInt(input.value, 10) || 1;
      input.value = v + 1;
    });
  }

  /* ─── Variant swatches ────────────────────────────────────── */
  function initVariants(root) {
    var form = $('[data-product-form]', root);
    if (!form) return;
    var handle = form.getAttribute('data-product-handle');
    if (!handle) return;

    var productData = null;
    function loadProduct() {
      if (productData) return Promise.resolve(productData);
      return fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (data) { productData = data; return data; });
    }

    function selectedOptions() {
      return $$('[data-option-group]', root).map(function (group) {
        // Size options render as a <select>; others as .swatch pills.
        var select = group.querySelector('[data-variant-select]');
        if (select) return select.value;
        var active = $('[data-swatch].active', group);
        return active ? active.getAttribute('data-value') : null;
      });
    }

    function findVariant(product) {
      var selected = selectedOptions();
      return product.variants.find(function (v) {
        return v.options.every(function (opt, i) {
          return !selected[i] || opt === selected[i];
        });
      });
    }

    function updateForVariant(variant) {
      var idInput = $('[data-variant-id]', root);
      var priceEl = $('[data-variant-price]', root);
      var afterpayEl = $('[data-variant-afterpay]', root);
      var compareEl = $('[data-variant-compare]', root);
      var atcBtn = $('[data-atc-button]', root);

      if (!variant) {
        if (atcBtn) {
          atcBtn.disabled = true;
          atcBtn.textContent = 'Unavailable';
        }
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
      if (afterpayEl) {
        var quarter = Math.round(variant.price / 4);
        afterpayEl.innerHTML = 'or 4 interest-free payments of <strong>' + formatMoney(quarter) + '</strong> with Afterpay';
      }
      if (atcBtn) {
        if (variant.available) {
          atcBtn.disabled = false;
          atcBtn.textContent = 'Add to cart';
        } else {
          atcBtn.disabled = true;
          atcBtn.textContent = 'Sold out';
        }
      }

      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {}
    }

    $$('[data-option-group]', root).forEach(function (group) {
      var optionIndex = parseInt(group.getAttribute('data-option-index'), 10);
      var labelValue = $('[data-variant-label-value][data-option-index="' + optionIndex + '"]', root);
      $$('[data-swatch]', group).forEach(function (sw) {
        sw.addEventListener('click', function () {
          $$('[data-swatch]', group).forEach(function (s) { s.classList.remove('active'); });
          sw.classList.add('active');
          if (labelValue) labelValue.textContent = '— ' + sw.getAttribute('data-value');
          loadProduct().then(function (p) { updateForVariant(findVariant(p)); });
        });
      });

      // Size options use a <select> instead of pills.
      var select = group.querySelector('[data-variant-select]');
      if (select) {
        select.addEventListener('change', function () {
          if (labelValue) labelValue.textContent = '— ' + select.value;
          loadProduct().then(function (p) { updateForVariant(findVariant(p)); });
        });
      }
    });
  }

  /* ─── Main add-to-cart ─────────────────────────────────────── */
  function initMainAtc(root) {
    var form = $('[data-product-form]', root);
    if (!form) return;
    var btn = $('[data-atc-button]', root);

    form.addEventListener('submit', function (e) {
      // Let the dynamic checkout / Shop Pay button (rendered by
      // {{ form | payment_button }}) submit normally to checkout.
      var submitter = e.submitter;
      if (submitter && submitter !== btn && !submitter.hasAttribute('data-atc-button')) {
        return;
      }
      e.preventDefault();
      if (btn && btn.disabled) return;

      var idInput = $('[data-variant-id]', root);
      var qtyInput = $('[data-qty-input]', root);
      var id = idInput ? idInput.value : new FormData(form).get('id');
      var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
      if (!id) return;

      // Required initial: if the selector is present, a letter MUST be chosen.
      // A plain `required` attribute can't stop an AJAX add, so block here.
      var initialWrap = $('[data-initial-select]', root);
      var initialValue = '';
      if (initialWrap) {
        var initInput = $('[data-initial-input]', initialWrap);
        initialValue = initInput && initInput.value ? initInput.value : '';
        if (!initialValue) {
          initialWrap.classList.add('error');
          var help = $('[data-initial-help]', initialWrap);
          if (help) help.textContent = 'Please choose your initial before adding to cart.';
          (initialWrap.querySelector('[data-initial-grid]') || initialWrap)
            .scrollIntoView({ block: 'center', behavior: 'smooth' });
          return;
        }
      }

      var body = { id: parseInt(id, 10), quantity: qty };
      if (initialValue) { body.properties = { Initial: initialValue }; }

      var restoreText = btn ? btn.textContent : '';
      function restore() {
        if (btn) { btn.disabled = false; btn.textContent = restoreText || 'Add to cart'; }
      }
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

      addToCart(body, 'bylucia-pdp-form', function (err) {
        restore();
        if (err && (err.description || err.message)) {
          console.warn('[pdp] add to cart failed:', err.description || err.message);
        }
      })
        .then(function (cart) { if (cart) restore(); })
        .catch(restore);
    });
  }

  /* ─── Initial selector (A–Z) ───────────────────────────────── */
  function initInitial(root) {
    var wrap = $('[data-initial-select]', root);
    if (!wrap) return;
    var input = $('[data-initial-input]', wrap);
    var label = $('[data-initial-label]', wrap);
    var help = $('[data-initial-help]', wrap);
    var helpDefault = help ? help.textContent : '';
    var pills = $$('[data-initial-pill]', wrap);

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        pills.forEach(function (p) {
          p.classList.remove('active');
          p.setAttribute('aria-checked', 'false');
        });
        pill.classList.add('active');
        pill.setAttribute('aria-checked', 'true');

        var letter = pill.getAttribute('data-letter');
        if (input) input.value = letter;
        if (label) label.textContent = '— ' + letter;
        wrap.classList.remove('error');
        if (help) help.textContent = helpDefault;
      });
    });
  }

  /* ─── FBT bundle ATC ───────────────────────────────────────── */
  function initFbtBundle(root) {
    var btn = $('[data-fbt-add-bundle]', root);
    if (!btn) return;
    btn.addEventListener('click', function () {
      var ids = (btn.getAttribute('data-variant-ids') || '').split(',').filter(Boolean);
      if (!ids.length) return;
      var restoreText = btn.textContent;
      function restore() { btn.disabled = false; btn.textContent = restoreText || 'Add all three to cart'; }
      btn.disabled = true;
      btn.textContent = 'Adding…';

      addToCart(
        { items: ids.map(function (id) { return { id: parseInt(id, 10), quantity: 1 }; }) },
        'bylucia-pdp-fbt',
        function () { restore(); }
      )
        .then(function (cart) { if (cart) restore(); })
        .catch(restore);
    });
  }

  function initAll() {
    var page = $('.pdp-page');
    if (!page) return;
    initGallery(page);
    initQty(page);
    initVariants(page);
    initInitial(page);
    initMainAtc(page);
    initFbtBundle(page);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
